import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { TierStats } from '../../types';
import { TIER_COLORS, TIER_LABELS } from './tierChartConfig';

interface TierBreakdownChartProps {
  stats: TierStats[];
  title?: string;
}

export function TierBreakdownChart({
  stats,
  title = 'Generation Tier Breakdown',
}: TierBreakdownChartProps) {
  const data = stats
    .filter((item) => item.count > 0)
    .map((item) => ({
      ...item,
      name: TIER_LABELS[item.tier],
      value: item.count,
      color: TIER_COLORS[item.tier],
    }));
  const total = stats.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <div className="w-7 h-7 rounded bg-violet-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
        <div className="ml-auto text-right">
          <p className="text-lg font-bold text-surface-700">{total}</p>
          <p className="text-[10px] text-surface-500 uppercase">Scans</p>
        </div>
      </div>

      {data.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="48%"
                innerRadius="35%"
                outerRadius="62%"
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${percent.toFixed(1)}%`}
                labelLine={{ stroke: '#64748b', strokeWidth: 1 }}
              >
                {data.map((item) => (
                  <Cell key={item.tier} fill={item.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value: number, name: string) => [`${value} scans`, name]}
              />
              <Legend
                verticalAlign="bottom"
                height={24}
                formatter={(value: string) => <span className="text-surface-600 text-xs">{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center text-surface-400">
          No tier data available
        </div>
      )}
    </div>
  );
}
